import forEach from 'lodash/forEach';
import isEmpty from 'lodash/isEmpty';
import isObject from 'lodash/isObject';
import { action, autorun, computed, flow, observable, reaction } from 'mobx';
import PubSub from 'pubsub-js';
import querystring from 'querystring';

import {
  CarouselMessage,
  EscalationMessages,
  HTMLMessage,
  HTMLMessageWithScroll,
  JsonSelectMessage,
  QuestionsMessage,
  QuestionsMessageProjects,
  RelatedQuestionsButton,
  TextMessage,
  TextMessageWithTranslation,
  TreeMessage
} from '../models';
import {
  API,
  checkQuestionInTags,
  detectEmptyAnswer,
  detectLanguageId,
  endpoints,
  getCookie,
  GPF_TYPE_TREE,
  handleError,
  handleRegex,
  isPriorityMessage,
  MESSAGE_TREE_TYPE_ALTERNATE,
  MESSAGE_TREE_TYPE_BRANCH,
  MESSAGE_TREE_TYPE_COMMENT,
  MESSAGE_TREE_TYPE_CONTEXTUAL,
  MESSAGE_TREE_TYPE_LINK_TO_MQA,
  MESSAGE_TREE_TYPE_LINK_TO_URL,
  MESSAGE_TREE_TYPE_REDIRECT,
  MESSAGE_TYPE_RECEIVED,
  MESSAGE_TYPE_SENT,
  PUBSUB_LIVE_CHAT_SUGGEST,
  PUBSUB_LIVE_CHAT_SUGGEST_THUMBSDOWN,
  separateMessageText,
  setCookie,
  sortGpfTree
} from '../utilities';

const constantsForRequest = {
  centralModule: 'Central Module',
  rephraseQuestion: 'Rephrase question',
  dateTime: '{datetime}',
  intentCarousel: '{intentCarousel}',
  intentCarouselWithJsonData: 'data-name="carousel"',
  jobSearch: '{jobSearchCarousel}',
  jobSearchLink: '{jobSearchURL}'
};

export default class BotStore {
  constructor(rootStore) {
    this.rootStore = rootStore;
    reaction(
      () => this.rootStore.chatWindow.isOpened,
      isOpened => {
        if (
          isOpened &&
          !this.rootStore.config.projectSelector?.enabled &&
          !this.rootStore.chatWindow.isCookie
        ) {
          if (this.rootStore.config.topFaqs.category) {
            this.getTopFAQsHotTopicsbyCategory({
              ProjectId:
                this.rootStore.ui.project.projectId > 0
                  ? this.rootStore.ui.project.projectId
                  : this.rootStore.config.id,
              Category: this.rootStore.config.topFaqs.category,
              Count: this.rootStore.config.topFaqs.count,
              Type: this.rootStore.config.topFaqs.type
            });
          } else if (this.rootStore.app.currentLanguage) {
            this.getTopFAQsLanguage({
              ProjectId:
                this.rootStore.ui.project.projectId > 0
                  ? this.rootStore.ui.project.projectId
                  : this.rootStore.config.id,
              Category: this.rootStore.config.topFaqs.category,
              Count: this.rootStore.config.topFaqs.count,
              Type: this.rootStore.config.topFaqs.type,
              TextLanguage: this.rootStore.app.currentLanguage
            });
          } else if (
            !this.rootStore.config.overlapMenu ||
            (this.rootStore.config.overlapMenu && !this.rootStore.config.overlapMenu.enabled)
          ) {
            this.getTopFaqs({
              ProjectId:
                this.rootStore.ui.project.projectId > 0
                  ? this.rootStore.ui.project.projectId
                  : this.rootStore.config.id,
              Category: this.rootStore.config.topFaqs.category,
              Count: this.rootStore.config.topFaqs.count,
              Type: this.rootStore.config.topFaqs.type
            });
          }
        }
      }
    );

    autorun(() => {
      /**
       * The user asks a question that the engine has
       * not been able to successfully match,
       * responding with ‘I have no matches’ x-number of times.
       */
      if (this.noAnswersCount >= Number(this.rootStore.config.escalationMessages.noAnswersCount)) {
        if (
          this.rootStore.config.liveAgentTriggerScenarios.scenarios &&
          this.rootStore.config.liveAgentTriggerScenarios.scenarios.showEscalation
        ) {
          window.messageFolderId = 0;
          PubSub.publish(PUBSUB_LIVE_CHAT_SUGGEST);
          this.resetNoAnswersCount();
        } else if (this.rootStore.config.escalationMessages.intentEscalation) {
          this.runGetMqaLanguageWithSpecialIntent(
            this.rootStore.config.escalationMessages.intentEscalation
          );
        } else {
          this.rootStore.chat.addMessage(
            new EscalationMessages({
              type: MESSAGE_TYPE_RECEIVED
            })
          );
        }
      }
      /**
       * Send a new getTopFAQsLanguage request, if projectSelector is enabled
       * and the projectId selected
       */
      if (
        this.rootStore.config.projectSelector?.enabled &&
        this.rootStore.ui.project.id > 0 &&
        !this.rootStore.ui.project.isSelected
      ) {
        this.rootStore.app.startSession();
        localStorage.setItem('MesIndexCount', 0);
        this.getTopFAQsLanguage({
          ProjectId: this.rootStore.ui.project.projectId,
          Category: this.rootStore.config.topFaqs.category,
          Type: this.rootStore.config.topFaqs.type,
          TextLanguage: this.rootStore.app.currentLanguage
        });
        this.rootStore.ui.setProjectSelected();
      }

      /**
       * Send a new getTopFAQsHotTopicsbyCategory request, if the OverlapMenu is enabled
       * and the categoryId exists
       */
      if (
        this.rootStore.config.overlapMenu &&
        this.rootStore.config.overlapMenu.enabled &&
        this.rootStore.ui.category.id
      ) {
        this.getTopFAQsHotTopicsbyCategory({
          ProjectId:
            this.rootStore.ui.project.projectId > 0
              ? this.rootStore.ui.project.projectId
              : this.rootStore.config.id,
          Category: this.rootStore.ui.category.id,
          Count: this.rootStore.config.topFaqs.count,
          Type: this.rootStore.config.topFaqs.type
        });
      }

      /**
       * Send a new getTopFAQsHotTopicsbyCategory request, if the HeaderMenu is enabled
       * and the categoryId exists
       */
      if (this.rootStore.config.headerMenu?.enabled && this.rootStore.ui.category.id) {
        this.getTopFAQsHotTopicsbyCategory({
          ProjectId:
            this.rootStore.ui.project.projectId > 0
              ? this.rootStore.ui.project.projectId
              : this.rootStore.config.id,
          Category: this.rootStore.ui.category.id,
          Count: this.rootStore.config.topFaqs.count,
          Type: this.rootStore.config.topFaqs.type
        });
      }

      /**
       * Send a new getTopFAQsLanguage request, if the language has changed
       */
      if (
        this.rootStore.app.isChangedLanguage &&
        this.rootStore.app.sessionId &&
        !this.rootStore.config.projectSelector?.enabled &&
        !this.rootStore.chatWindow.isCookie
      ) {
        if (this.rootStore.chat.isSpecialMessage) {
          this.rootStore.ui.setMessageInputDisabled(true);
          this.runGetMqaLanguageWithSpecialIntent(this.rootStore.config.emailStopper.intent);
        }
        this.getTopFAQsLanguage({
          ProjectId:
            this.rootStore.ui.project.projectId > 0
              ? this.rootStore.ui.project.projectId
              : this.rootStore.config.id,
          Category: this.rootStore.config.topFaqs.category,
          Count: this.rootStore.config.topFaqs.count,
          Type: this.rootStore.config.topFaqs.type,
          TextLanguage: this.rootStore.app.currentLanguage
        });
      }
    });
  }

  @observable topFrequentQuestions = [];

  @observable isDoneTopFAQsRequest = false;

  @observable noAnswersCount = 0;

  @observable sameAnswersCount = 0;

  @observable lastResponseId;

  @observable relatedQuestionsForButton = [];

  @observable configProjectName = [];

  @observable categoryId = 0;

  @observable parentCategoryId = 0;

  @observable isSpecialIntent = 0;

  @observable formFields = {
    input_name: ''
  };

  @observable jsonSelectList = {};

  @action setLastResponseId(id) {
    if (this.lastResponseId === id) {
      this.sameAnswersCount += 1;
    } else {
      this.resetNoAnswersCount();
    }

    this.lastResponseId = id;
  }

  @action resetNoAnswersCount() {
    this.noAnswersCount = 0;
  }

  @action resetSameAnswersCount() {
    this.sameAnswersCount = 0;
  }

  @action setParentCategoryId = id => {
    this.parentCategoryId = id;
  };

  @computed get hotTopics() {
    if (this.rootStore.config.topFaqs.hotTopicsCount === 0) {
      return [];
    }
    if (
      this.rootStore.config.topFaqs.hotTopicsCount &&
      this.rootStore.config.topFaqs.hotTopicsCount > 0
    ) {
      return this.topFrequentQuestions
        .filter(question => question.Description === 'HotTopics')
        .slice(0, this.rootStore.config.topFaqs.hotTopicsCount);
    }
    return this.topFrequentQuestions.filter(question => question.Description === 'HotTopics');
  }

  @computed get topQuestions() {
    return this.topFrequentQuestions.filter(question => question.Description === 'TopFAQs');
  }

  /**
   * This function calculates the number of messages at startup of chat or
   * after clearing messages
   */
  @computed get firstMessagesNumber() {
    /**
     * WM - welcome message
     * HT - hot topics,
     * PQ - popular questions.
     */
    // WM, HT, PQ
    if (
      this.rootStore.config.message.welcomeMessage &&
      this.rootStore.config.message.hotTopics.enabled &&
      this.hotTopics.length &&
      this.rootStore.config.message.popularQuestions.enabled &&
      this.topQuestions.length
    ) {
      return 3;
    }
    // WM, HT || PQ
    if (
      this.rootStore.config.message.welcomeMessage &&
      ((this.rootStore.config.message.hotTopics.enabled && this.hotTopics.length) ||
        (this.rootStore.config.message.popularQuestions.enabled && this.topQuestions.length))
    ) {
      return 2;
    }
    // !WM, HT, PQ
    if (
      !this.rootStore.config.message.welcomeMessage &&
      this.rootStore.config.message.hotTopics.enabled &&
      this.hotTopics.length &&
      this.rootStore.config.message.popularQuestions.enabled &&
      this.topQuestions.length
    ) {
      return 2;
    }
    // !WM, HT || PQ
    if (
      !this.rootStore.config.message.welcomeMessage &&
      ((this.rootStore.config.message.hotTopics.enabled && this.hotTopics.length) ||
        (this.rootStore.config.message.popularQuestions.enabled && this.topQuestions.length))
    ) {
      return 1;
    }
    return 0;
  }

  getTopFaqs = flow(function* getTopFaqs({ ProjectId, Category, Count, Type }) {
    if (this.topFrequentQuestions.length > 0 || this.rootStore.ui.category.id) {
      return;
    }

    const response = yield API.post(
      endpoints.paths.getTopFaqs,
      querystring.stringify({ ProjectId, Category, Count, Type })
    );

    this.isDoneTopFAQsRequest = true;

    if (response && response.ArrayOfTopFrequentQuestion) {
      const { ArrayOfTopFrequentQuestion } = response;

      // response has an array
      if (
        ArrayOfTopFrequentQuestion &&
        ArrayOfTopFrequentQuestion.TopFrequentQuestion &&
        ArrayOfTopFrequentQuestion.TopFrequentQuestion.length
      ) {
        this.topFrequentQuestions = ArrayOfTopFrequentQuestion.TopFrequentQuestion;
      }

      // response has an object
      if (
        ArrayOfTopFrequentQuestion &&
        ArrayOfTopFrequentQuestion.TopFrequentQuestion &&
        isObject(ArrayOfTopFrequentQuestion.TopFrequentQuestion) &&
        !isEmpty(ArrayOfTopFrequentQuestion.TopFrequentQuestion)
      ) {
        this.topFrequentQuestions.push(ArrayOfTopFrequentQuestion.TopFrequentQuestion);
      }
    }
  });

  getTopFAQsLanguage = flow(function* getTopFAQsLanguage({
    ProjectId,
    Category,
    Count,
    Type,
    TextLanguage
  }) {
    if (
      (this.topFrequentQuestions.length > 0 && !this.rootStore.app.isChangedLanguage) ||
      this.rootStore.ui.category.id
    ) {
      return;
    }

    this.rootStore.app.setIsChangeLanguage(false, this.rootStore.app.currentLanguage);
    const response = yield API.post(
      endpoints.paths.getTopFAQsLanguage,
      querystring.stringify({ ProjectId, Category, Count, Type, TextLanguage })
    );

    this.isDoneTopFAQsRequest = true;

    if (response && response.ArrayOfTopFrequentQuestion) {
      const { ArrayOfTopFrequentQuestion } = response;

      // response has an array
      if (
        ArrayOfTopFrequentQuestion &&
        ArrayOfTopFrequentQuestion.TopFrequentQuestion &&
        ArrayOfTopFrequentQuestion.TopFrequentQuestion.length
      ) {
        this.topFrequentQuestions = ArrayOfTopFrequentQuestion.TopFrequentQuestion;
      }

      // response has an object
      if (
        ArrayOfTopFrequentQuestion &&
        ArrayOfTopFrequentQuestion.TopFrequentQuestion &&
        isObject(ArrayOfTopFrequentQuestion.TopFrequentQuestion) &&
        !isEmpty(ArrayOfTopFrequentQuestion.TopFrequentQuestion)
      ) {
        this.topFrequentQuestions.push(ArrayOfTopFrequentQuestion.TopFrequentQuestion);
      }
    }
  });

  getTopFAQsHotTopicsbyCategory = flow(function* getTopFAQsHotTopicsbyCategory({
    ProjectId,
    Category,
    Count,
    Type
  }) {
    if (this.rootStore.ui.category.id && this.rootStore.ui.category.id === this.categoryId) return;

    if (this.rootStore.config.topFaqs.category && this.topFrequentQuestions.length > 0) return;

    this.topFrequentQuestions = [];
    this.categoryId = this.rootStore.ui.category.id;

    const response = yield API.post(
      endpoints.paths.getTopFAQsHotTopicsbyCategory,
      querystring.stringify({ ProjectId, Category, Count, Type })
    );

    if (response && response.ArrayOfTopFrequentQuestion) {
      const { ArrayOfTopFrequentQuestion } = response;

      // response has an array
      if (
        ArrayOfTopFrequentQuestion &&
        ArrayOfTopFrequentQuestion.TopFrequentQuestion &&
        ArrayOfTopFrequentQuestion.TopFrequentQuestion.length
      ) {
        this.topFrequentQuestions = ArrayOfTopFrequentQuestion.TopFrequentQuestion;
      }

      // response has an object
      if (
        ArrayOfTopFrequentQuestion &&
        ArrayOfTopFrequentQuestion.TopFrequentQuestion &&
        isObject(ArrayOfTopFrequentQuestion.TopFrequentQuestion) &&
        !isEmpty(ArrayOfTopFrequentQuestion.TopFrequentQuestion)
      ) {
        this.topFrequentQuestions.push(ArrayOfTopFrequentQuestion.TopFrequentQuestion);
      }
    }
  });

  getGpfId = flow(function* getGpfId({ ProjectId, MesId }) {
    const response = yield API.post(
      endpoints.paths.getGpfId,
      querystring.stringify({ ProjectId, MesId })
    );

    if (response && response.ArrayOfGPFColl && response.ArrayOfGPFColl.GPFColl) {
      const { GPFColl } = response.ArrayOfGPFColl;
      return GPFColl;
    }

    return undefined;
  });

  getGpfTree = flow(function* getGpfTree({
    ProjectId,
    MesId,
    GPFId,
    ParentNodeId,
    Source,
    SessionId,
    SourceLanguage
  }) {
    const response = yield API.post(
      endpoints.paths.getGpfTree,
      querystring.stringify({
        ProjectId,
        MesId,
        GPFId,
        ParentNodeId,
        Source,
        SessionId,
        SourceLanguage
      })
    );

    if (response && response.ArrayOfGPFColl && response.ArrayOfGPFColl.GPFColl) {
      const { GPFColl } = response.ArrayOfGPFColl;

      return GPFColl;
    }

    return undefined;
  });

  getMqa = flow(function* getMqa({
    ProjectId,
    MesId,
    Source,
    SessionId,
    CrossProjectName,
    TextLanguage
  }) {
    const response = this.rootStore.app.currentLanguage
      ? yield API.post(
          endpoints.paths.getMqaLanguage,
          querystring.stringify({
            ProjectId,
            MesId,
            Source,
            SessionId,
            TextLanguage: TextLanguage || this.rootStore.app.currentLanguage
          })
        )
      : yield API.post(
          endpoints.paths.getMqa,
          querystring.stringify({
            ProjectId,
            MesId,
            Source,
            SessionId
          })
        );

    if (response && response.ArrayOfGPFColl && response.ArrayOfGPFColl.GPFColl) {
      const { GPFColl } = response.ArrayOfGPFColl;
      const isEmptyAnswer = detectEmptyAnswer(GPFColl.ResponseText);

      if (isEmptyAnswer) {
        return undefined;
      }

      return { ...GPFColl, CrossProjectName };
    }

    return undefined;
  });

  getMqaLanguageCarousel = flow(function* getMqaLanguageCarousel({
    ProjectId,
    MesId,
    Source,
    SessionId,
    CrossProjectName,
    TextLanguage
  }) {
    const response = yield API.post(
      endpoints.paths.getMqaLanguageCarousel,
      querystring.stringify({
        ProjectId,
        MesId,
        Source,
        SessionId,
        TextLanguage
      })
    );

    if (response && response.ArrayOfGPFColl && response.ArrayOfGPFColl.GPFColl) {
      const { GPFColl } = response.ArrayOfGPFColl;
      const isEmptyAnswer = detectEmptyAnswer(GPFColl.ResponseText);

      if (isEmptyAnswer) {
        return undefined;
      }

      return { ...GPFColl, CrossProjectName };
    }

    return undefined;
  });

  runGpfSequence = flow(function* runGpfSequence({
    ProjectId,
    MesId,
    Source,
    SessionId,
    SourceLanguage,
    CrossProjectName
  }) {
    try {
      const gpfId = yield this.getGpfId({ ProjectId, MesId });
      if (gpfId && gpfId.GPFType === GPF_TYPE_TREE) {
        const tree = yield this.getGpfTree({
          ProjectId,
          MesId: gpfId.MesID,
          GPFId: gpfId.GPFID,
          ParentNodeId: gpfId.RootNodeID,
          Source,
          SessionId,
          SourceLanguage
        });

        if (tree) {
          if (Array.isArray(tree)) {
            const sortedTree = sortGpfTree(tree);
            sortedTree.map(treeItem => {
              // comment
              if (treeItem.Type === MESSAGE_TREE_TYPE_COMMENT) {
                if (treeItem.Comment) {
                  this.rootStore.chat.addMessage(
                    new TreeMessage({
                      type: MESSAGE_TYPE_RECEIVED,
                      content: treeItem,
                      original: {
                        crossProjectNameTree: CrossProjectName
                      }
                    })
                  );
                }
              }

              // branches
              if (
                treeItem.Type !== MESSAGE_TREE_TYPE_COMMENT &&
                treeItem.Type !== MESSAGE_TREE_TYPE_ALTERNATE &&
                treeItem.Type !== MESSAGE_TREE_TYPE_REDIRECT &&
                treeItem.Type !== MESSAGE_TREE_TYPE_CONTEXTUAL &&
                treeItem.branches.length
              ) {
                this.rootStore.chat.addMessage(
                  new TreeMessage({
                    type: MESSAGE_TYPE_RECEIVED,
                    content: treeItem.branches,
                    original: {
                      crossProjectNameTree: CrossProjectName,
                      language: SourceLanguage
                    }
                  })
                );
              }
            });
          } else {
            this.rootStore.chat.addMessage(
              new TreeMessage({
                type: MESSAGE_TYPE_RECEIVED,
                content: tree,
                original: {
                  crossProjectNameTree: CrossProjectName,
                  language: SourceLanguage
                }
              })
            );
          }
        }
      }
    } catch (error) {
      this.rootStore.app.log({ Message: handleError(error) });
    }
  });

  runMqaSequence = flow(function* runMqaSequence({
    ProjectId,
    MesId,
    Source,
    SessionId,
    SourceLanguage,
    CrossProjectName,
    Question
  }) {
    const intentForEnabledMessageInput = this.rootStore.config.messageInput.onInit
      ? this.rootStore.config.messageInput.onInit.intentOnEnable
      : 0;

    if (intentForEnabledMessageInput === MesId) {
      this.rootStore.ui.setMessageInputDisabled(false);
    }

    // Suggest the live chat on special intent
    if (this.rootStore.config.liveAgentTriggerScenarios.scenarios) {
      const isSpecialIntentForLiveChat =
        MesId === this.rootStore.config.liveAgentTriggerScenarios.scenarios.showOnIntent
          ? this.rootStore.config.liveAgentTriggerScenarios.scenarios.showOnIntent
          : 0;
      if (isSpecialIntentForLiveChat) {
        window.messageFolderId = 0;
        PubSub.publish(PUBSUB_LIVE_CHAT_SUGGEST, SourceLanguage);
        return null;
      }
    }

    // Suggest the live chat on dept intent
    if (this.rootStore.config.liveAgentTriggerScenarios.scenarios.showOnDeptIntent?.length) {
      const deptIntents = this.rootStore.config.liveAgentTriggerScenarios.scenarios
        .showOnDeptIntent;
      let isMatch = false;
      forEach(deptIntents, intents => {
        if (intents.intent === MesId) {
          isMatch = true;
          window.messageFolderId = intents.folderid;
          PubSub.publish(PUBSUB_LIVE_CHAT_SUGGEST, SourceLanguage);
        }
      });
      if (isMatch) {
        return null;
      }
    }

    const intentUserInput = this.detectIntentUserInput(MesId);
    const isJobSearchIntent = this.detectJobSearch(MesId);

    try {
      let jsc;
      if (isJobSearchIntent) {
        const jsResponse = yield this.jobSearch();
        jsc = jsResponse;
      }

      const mqa = yield this.getMqa({
        ProjectId,
        MesId: intentUserInput || MesId,
        Source,
        SessionId,
        CrossProjectName
      });

      this.rootStore.chat.setLiveChatTriggers('', '');
      if (mqa && mqa.ResponseText) {
        const specialSelect = this.detecttJsonSelect(MesId);
        const isTextWithSpecialId = this.detectMultiResponse(mqa.ResponseText);
        const intentCarousel = this.detectIntentCarousel(MesId);
        const isIntentCarouselText = mqa.ResponseText.includes(constantsForRequest.intentCarousel);
        const isJobSearchText = mqa.ResponseText.includes(constantsForRequest.jobSearch);
        const internalNotesText =
          this.rootStore.config.showInternalNotes &&
          this.rootStore.config.showInternalNotes.enabled &&
          mqa.ResponseTextNotes
            ? mqa.ResponseTextNotes
            : '';
        const isShowOnTag = this.rootStore.config.liveAgentTriggerScenarios.scenarios.showOnTag
          ? mqa.ResponseText.includes(
              this.rootStore.config.liveAgentTriggerScenarios.scenarios.showOnTag.tagName
            )
          : false;

        if (!isEmpty(specialSelect)) {
          this.rootStore.chat.addMessage(
            new JsonSelectMessage({
              type: MESSAGE_TYPE_RECEIVED,
              content: {
                data: specialSelect,
                text: mqa.ResponseText
              }
            })
          );
        } else if (isShowOnTag) {
          const liveChatTagOnline = this.rootStore.config.liveAgentTriggerScenarios.scenarios
            .showOnTag.tagOnline;
          const liveChatTagOffline = this.rootStore.config.liveAgentTriggerScenarios.scenarios
            .showOnTag.tagOffline;
          const liveChatTriggerId = this.rootStore.config.liveAgentTriggerScenarios.scenarios
            .showOnTag.tagTriggerId;
          const liveChatCatId = this.rootStore.config.liveAgentTriggerScenarios.scenarios.showOnTag
            .tagCatId;
          const getAns = mqa.ResponseText;
          const parser = new DOMParser();
          const html = parser.parseFromString(getAns, 'text/html');
          const ansObject = html.body;
          const onlineMsg = ansObject.querySelector(`#${liveChatTagOnline}`).innerHTML;
          const offlineMsg = ansObject.querySelector(`#${liveChatTagOffline}`).innerHTML;
          this.rootStore.chat.setLiveChatTag(onlineMsg, offlineMsg);

          const getNotes = mqa.ResponseTextNotes;
          const htmlNotes = parser.parseFromString(getNotes, 'text/html');
          const notesObject = htmlNotes.body;
          const catId = notesObject.querySelector(`#${liveChatCatId}`)
            ? notesObject.querySelector(`#${liveChatCatId}`).innerHTML
            : '';
          const triggerId = notesObject.querySelector(`#${liveChatTriggerId}`)
            ? notesObject.querySelector(`#${liveChatTriggerId}`).innerHTML
            : '';
          this.rootStore.chat.setLiveChatTriggers(catId, triggerId);
          PubSub.publish(PUBSUB_LIVE_CHAT_SUGGEST);
        } else if (!isEmpty(intentCarousel) && !isIntentCarouselText) {
          // show intent with the carousel
          this.rootStore.chat.addMessage(
            new CarouselMessage({
              type: MESSAGE_TYPE_RECEIVED,
              content: {
                content: intentCarousel.content
              }
            })
          );
        } else if (isJobSearchIntent && !isJobSearchText) {
          if (typeof jsc === 'string') {
            this.rootStore.chat.addMessage(
              new HTMLMessage({
                type: MESSAGE_TYPE_RECEIVED,
                content: jsc,
                original: {
                  ...mqa,
                  crossProjectName: CrossProjectName
                }
              })
            );
          } else {
            this.rootStore.chat.addMessage(
              new CarouselMessage({
                type: MESSAGE_TYPE_RECEIVED,
                original: {
                  isShowMessageDate: false,
                  jobSearch: true
                },
                content: {
                  content: jsc
                }
              })
            );
          }
        } else if (isTextWithSpecialId) {
          // create HTML messages with the special id (multiResponse)
          if (isJobSearchIntent && typeof jsc === 'string') {
            this.rootStore.chat.addMessage(
              new HTMLMessage({
                type: MESSAGE_TYPE_RECEIVED,
                content: jsc,
                original: {
                  ...mqa,
                  crossProjectName: CrossProjectName
                }
              })
            );
          } else {
            const sourceLanguage = SourceLanguage === 'en' ? 'en-GB' : SourceLanguage;
            yield this.showMessageWithSpecialId(
              mqa,
              mqa.ResponseText,
              CrossProjectName,
              mqa.MesID,
              0,
              jsc,
              sourceLanguage
            );
          }
        } else {
          this.rootStore.chat.addMessage(
            new HTMLMessage({
              type: MESSAGE_TYPE_RECEIVED,
              content: mqa.ResponseText,
              original: {
                ...mqa,
                crossProjectName: CrossProjectName,
                internalNotes: internalNotesText
              }
            })
          );
        }
      }

      yield this.runGpfSequence({
        ProjectId,
        MesId: intentUserInput || MesId,
        Source,
        SessionId,
        SourceLanguage,
        CrossProjectName
      });

      // insertChatHistory request
      if (
        this.rootStore.config.insertChatHistory &&
        this.rootStore.config.insertChatHistory.enabled
      ) {
        let insertQuestion;
        if (this.rootStore.config.sendEmail?.enabled) {
          insertQuestion = Question
            ? `<p>${Question || mqa.MessageText}</p>`
            : `<p><strong>User Viewed:</strong></p><p>${Question || mqa.MessageText}</p>`;
        } else insertQuestion = Question || mqa.MessageText;

        const insertResponse = this.rootStore.config.sendEmail?.enabled
          ? `<p><strong>${this.rootStore.config.sendEmail.projectName} says:</strong></p><p>${mqa.ResponseText}</p>`
          : mqa.ResponseText;

        this.insertChatHistory({
          MesId,
          Question: insertQuestion,
          Response: insertResponse
        });
      }
    } catch (error) {
      this.rootStore.app.log({ Message: handleError(error) });
    } finally {
      this.rootStore.chat.isMessaging = false;
    }
  });

  processCrossProject = flow(function* processCrossProject({
    MainProjectMesId,
    CrossProjectMesId,
    MainProjectName
  }) {
    const response = yield API.post(
      endpoints.paths.processCrossProject,
      querystring.stringify({ MainProjectMesId, CrossProjectMesId, MainProjectName })
    );

    if (response) {
      return response;
    }

    return undefined;
  });

  recordCrossAgencyQuery = flow(function* recordCrossAgencyQuery({ MesId, TLA }) {
    const response = yield API.post(
      endpoints.paths.recordCrossAgencyQuery,
      querystring.stringify({
        MesId,
        TLA
      })
    );

    return response;
  });

  insertChatHistory = flow(function* insertChatHistory({ MesId, Question, Response }) {
    const formatQuestion = `<p>${Question}</p>`;
    const response = yield API.post(
      endpoints.paths.insertChatHistory,
      JSON.stringify({
        ProjectId:
          this.rootStore.ui.project.projectId > 0
            ? this.rootStore.ui.project.projectId
            : this.rootStore.config.id,
        SessionID: this.rootStore.app.sessionId,
        MesId,
        Question: formatQuestion,
        Response
      })
    );

    return response;
  });

  runInfoSequence = flow(function* runInfoSequence({
    MessageText,
    MesId,
    SourceLanguage,
    CrossProjectName
  }) {
    this.rootStore.chat.addMessage(
      new TextMessage({
        type: MESSAGE_TYPE_SENT,
        content: MessageText,
        original: {
          crossProjectName: CrossProjectName
        }
      })
    );

    this.rootStore.chat.isMessaging = true;

    try {
      yield this.runMqaSequence({
        MesId,
        SourceLanguage,
        Source:
          this.rootStore.ui.project.source !== ''
            ? this.rootStore.ui.project.source
            : this.rootStore.config.source,
        SessionId: this.rootStore.app.sessionId,
        ProjectId:
          this.rootStore.ui.project.projectId > 0
            ? this.rootStore.ui.project.projectId
            : this.rootStore.config.id,
        CrossProjectName
      });
    } catch (error) {
      this.rootStore.app.log({ Message: handleError(error) });
    } finally {
      this.rootStore.chat.isMessaging = false;
    }
  });

  runTreeSequence = flow(function* runTreeSequence({
    ProjectId,
    MesId,
    GPFId,
    ParentNodeId,
    SourceLanguage,
    Comment,
    CrossProjectName,
    hideEmptyAnswer
  }) {
    if (Comment) {
      this.rootStore.chat.addMessage(
        new TextMessage({
          type: MESSAGE_TYPE_SENT,
          content: String(Comment),
          original: {
            crossProjectName: CrossProjectName
          }
        })
      );
    }

    this.rootStore.chat.isMessaging = true;
    let isAlternatives = false;

    try {
      const tree = yield this.getGpfTree({
        ProjectId,
        MesId,
        GPFId,
        ParentNodeId,
        Source:
          this.rootStore.ui.project.source !== ''
            ? this.rootStore.ui.project.source
            : this.rootStore.config.source,
        SessionId: this.rootStore.app.sessionId,
        SourceLanguage
      });

      const isHideEmptyAnswer = hideEmptyAnswer || false;
      if (!tree && !isHideEmptyAnswer) {
        this.rootStore.chat.addMessage(
          new TextMessage({
            type: MESSAGE_TYPE_RECEIVED,
            content: 'No response'
          })
        );
      }

      if (Array.isArray(tree)) {
        const sortedTree = sortGpfTree(tree);
        const multilevelFlowData = {};
        sortedTree.map(item => {
          // comment
          if (item.Type === MESSAGE_TREE_TYPE_COMMENT) {
            if (item.Comment) {
              this.rootStore.chat.addMessage(
                new TreeMessage({
                  type: MESSAGE_TYPE_RECEIVED,
                  content: item,
                  original: {
                    crossProjectNameTree: CrossProjectName
                  }
                })
              );
            }
          }

          // context
          if (item.Type === MESSAGE_TREE_TYPE_CONTEXTUAL) {
            multilevelFlowData.gpfId = item.MainGPFID;
            multilevelFlowData.parentNodeId = item.GPFToId;
            // multilevelFlowData.comment = item.Comment;
          }

          // alternative
          if (item.Type === MESSAGE_TREE_TYPE_ALTERNATE) {
            isAlternatives = true;
            this.rootStore.chat.isMessaging = true;
            return this.askQuestion({
              Question: item.Comment,
              TextLanguage: SourceLanguage,
              hideUserMessage: true,
              multilevelFlowData
            });
          }

          // MQA
          if (item.Type === MESSAGE_TREE_TYPE_LINK_TO_MQA) {
            return this.runMqaSequence({
              ProjectId,
              MesId: item.Data,
              Source:
                this.rootStore.ui.project.source !== ''
                  ? this.rootStore.ui.project.source
                  : this.rootStore.config.source,
              SessionId: this.rootStore.app.sessionId,
              SourceLanguage,
              CrossProjectName,
              Question: item.Comment
            });
          }

          // branches
          if (
            item.Type !== MESSAGE_TREE_TYPE_COMMENT &&
            item.Type !== MESSAGE_TREE_TYPE_ALTERNATE &&
            item.Type !== MESSAGE_TREE_TYPE_CONTEXTUAL &&
            item.Type !== MESSAGE_TREE_TYPE_REDIRECT &&
            item.Type !== MESSAGE_TREE_TYPE_LINK_TO_MQA
          ) {
            this.rootStore.chat.addMessage(
              new TreeMessage({
                type: MESSAGE_TYPE_RECEIVED,
                content: item.branches,
                original: {
                  crossProjectNameTree: CrossProjectName,
                  language: SourceLanguage
                }
              })
            );
          }
        });
      }

      if (!Array.isArray(tree) && tree.Type === MESSAGE_TREE_TYPE_BRANCH) {
        const treeArray = [];
        treeArray.push(tree);
        const sortedTree = sortGpfTree(treeArray);
        sortedTree.forEach(item => {
          if (
            item.Type !== MESSAGE_TREE_TYPE_COMMENT &&
            item.Type !== MESSAGE_TREE_TYPE_ALTERNATE &&
            item.Type !== MESSAGE_TREE_TYPE_CONTEXTUAL &&
            item.Type !== MESSAGE_TREE_TYPE_REDIRECT
          ) {
            this.rootStore.chat.addMessage(
              new TreeMessage({
                type: MESSAGE_TYPE_RECEIVED,
                content: item.branches,
                original: {
                  crossProjectNameTree: CrossProjectName,
                  language: SourceLanguage
                }
              })
            );
          }
        });
      }

      // TODO: need to check MESSAGE_TREE_TYPE_LINK_TO_URL
      if (tree.Type === MESSAGE_TREE_TYPE_LINK_TO_URL || tree.Type === MESSAGE_TREE_TYPE_COMMENT) {
        // insertChatHistory request
        if (
          this.rootStore.config.insertChatHistory &&
          this.rootStore.config.insertChatHistory.enabled
        ) {
          const insertQuestion = this.rootStore.config.sendEmail?.enabled
            ? `<p><strong>User Clicked:</strong></p><p>${Comment}</p>`
            : Comment;

          const insertResponse = this.rootStore.config.sendEmail?.enabled
            ? `<p><strong>${this.rootStore.config.sendEmail.projectName} says:</strong></p><p>${tree.Comment}</p>`
            : tree.Comment;

          this.insertChatHistory({ MesId, Question: insertQuestion, Response: insertResponse });
        }
        return this.rootStore.chat.addMessage(
          new TreeMessage({
            type: MESSAGE_TYPE_RECEIVED,
            content: tree,
            original: {
              crossProjectName: CrossProjectName
            }
          })
        );
      }

      if (tree.Type === MESSAGE_TREE_TYPE_ALTERNATE) {
        return yield this.askQuestion({
          Question: tree.Comment,
          TextLanguage: SourceLanguage,
          hideUserMessage: true
        });
      }

      if (tree.Type === MESSAGE_TREE_TYPE_REDIRECT) {
        const callBackURL = window.location.href;
        const url = tree.Comment;
        const prodType = url.indexOf('action=') >= 0 ? url.split('action=').pop() : '';

        const redirectError = () => {
          this.rootStore.chat.addMessage(
            new TextMessageWithTranslation({
              type: MESSAGE_TYPE_RECEIVED,
              content: this.rootStore.config.escalationMessages.redirectURL
            })
          );
        };

        const requestOptions = {
          method: 'GET',
          redirect: 'follow'
        };

        fetch(
          `${endpoints.paths.insertOneLogin}?ClientSessionID=${this.rootStore.app.sessionId}&MesId=${tree.MesID}&ProjectId=${this.rootStore.config.id}&ProductType=${prodType}&Url=${url}&Callback_Url=${callBackURL}`,
          requestOptions
        )
          .then(response => response.text())
          .then(result => {
            if (result === '1') {
              if (!getCookie('redirectURL_cookie')) {
                setCookie('redirectURL_cookie', this.rootStore.app.sessionId, 1);
                const getChatHist = document.querySelector('#messages-content').innerHTML;
                localStorage.setItem(
                  'redirectURL_chatHist',
                  `<div id="chatHist">${getChatHist}</div>`
                );
              }

              const queryString = Buffer.from(
                JSON.stringify({
                  SessionID: this.rootStore.app.sessionId,
                  CallBack_url: callBackURL
                })
              ).toString('Base64');

              if (url.indexOf('?') >= 0) window.location.href = `${url}&M1_callback=${queryString}`;
              else window.location.href = `${url}?M1_callback=${queryString}`;
            } else redirectError();
          })
          .catch(() => redirectError());
      }

      if (tree.Data) {
        yield this.runMqaSequence({
          ProjectId,
          MesId: tree.Data,
          Source:
            this.rootStore.ui.project.source !== ''
              ? this.rootStore.ui.project.source
              : this.rootStore.config.source,
          SessionId: this.rootStore.app.sessionId,
          SourceLanguage,
          CrossProjectName,
          Question: Comment
        });
      }
    } catch (error) {
      this.rootStore.app.log({ Message: handleError(error) });
    } finally {
      if (!isAlternatives) {
        this.rootStore.chat.isMessaging = false;
      }
    }
  });

  askWsLanguage = flow(function* askWsLanguage({
    ProjectId,
    Question,
    RecordQuestion,
    SessionId,
    TextLanguage,
    Source,
    ChannelId
  }) {
    try {
      const response = yield API.post(
        endpoints.paths.askWsLanguage,
        querystring.stringify({
          ProjectId,
          Question,
          RecordQuestion,
          SessionId,
          TextLanguage,
          Source,
          ChannelId
        })
      );

      if (response && response.ResponderResponseSet) {
        return response.ResponderResponseSet;
      }

      return false;
    } catch (e) {
      return false;
    }
  });

  askWSLanguagebyCategory = flow(function* askWSLanguagebyCategory({
    ProjectId,
    Question,
    RecordQuestion,
    SessionId,
    TextLanguage,
    CategoryId,
    Source
  }) {
    try {
      const response = yield API.post(
        endpoints.paths.askWSLanguagebyCategory,
        querystring.stringify({
          ProjectId,
          Question,
          RecordQuestion,
          SessionId,
          TextLanguage,
          CategoryId,
          Source,
          CategoryOption: this.rootStore.config.AskWSLanguagePublicCustomSearch
            ? this.rootStore.config.AskWSLanguagePublicCustomSearch.CategoryOption
            : 0
        })
      );

      if (response && response.ResponderResponseSet) {
        return response.ResponderResponseSet;
      }

      return false;
    } catch (e) {
      return false;
    }
  });

  /**
   * This method is called when the user asks a question using voice or manual input
   * and has 3 sequences:
   *
   * ⭐ [1] askWsLanguage
   *  - most of the logic is aimed at parsing and composing
   *  - a message with related questions for the user.
   * ⭐ [2] gpf sequence
   * ⭐ [3] cross project sequence (in case if no response from askWsLanguage)
   *
   * Also there is an implementation of 3 scenarios:
   *
   * - [1] live chat trigger scenario;
   * - [2] same answers scenario;
   * - [3] no answers scenario;
   *
   * @todo get code from onQuestionSubmit
   * @todo implement sameAnswers
   * @todo ⛔ description and refactoring is needed
   */

  askQuestion = flow(function* askQuestion({
    ProjectId,
    ProjectName,
    Question,
    UserText,
    TextLanguage,
    MainProjectMesId,
    hideUserMessage,
    multilevelFlowData
  }) {
    const configProjectName = this.rootStore.config.source.split('_')[0];
    const configProjectId =
      this.rootStore.ui.project.projectId > 0
        ? this.rootStore.ui.project.projectId
        : this.rootStore.config.id;
    const isHideUserMessage = hideUserMessage || false;

    // display user message
    if (!isHideUserMessage) {
      this.rootStore.chat.addMessage(
        new TextMessage({
          content: UserText || Question,
          type: MESSAGE_TYPE_SENT,
          original: {
            crossProjectName: ProjectName
          }
        })
      );
    }

    if (
      this.rootStore.config.insertChatHistory &&
      this.rootStore.config.insertChatHistory.enabled &&
      this.rootStore.config.sendEmail?.enabled
    ) {
      const insertQuestion = `<p><strong>User Asked:</strong></p><p>${UserText || Question}</p>`;

      this.insertChatHistory({
        Question: insertQuestion,
        Response: ''
      });
    }

    // enable messaging state
    this.rootStore.chat.isMessaging = true;

    // detect categoryId, categoryIds, parentCategoryId
    const categoryId = this.rootStore.ui.category.id;

    const categorieIds =
      this.rootStore.config.overlapMenu &&
      this.rootStore.config.overlapMenu.enabled &&
      this.rootStore.config.overlapMenu.askWsLanguageCategories &&
      categoryId
        ? `${this.rootStore.config.overlapMenu.askWsLanguageCategories},${categoryId}`
        : null;

    const askWSLanguagebyCategoryId =
      this.rootStore.config.askWSLanguagebyCategory &&
      this.rootStore.config.askWSLanguagebyCategory.enabled
        ? this.rootStore.config.askWSLanguagebyCategory.category
        : 0;

    const currentCategoryId =
      categorieIds || categoryId || askWSLanguagebyCategoryId || this.parentCategoryId;

    try {
      // ⭐ [1] call ask ws language or ask ws language by category endpoint
      const response = currentCategoryId
        ? yield this.askWSLanguagebyCategory({
            SessionId: this.rootStore.app.sessionId,
            ProjectId: configProjectId,
            Question,
            RecordQuestion: 'yes',
            TextLanguage,
            CategoryId: currentCategoryId,
            Source:
              this.rootStore.ui.project.source !== ''
                ? this.rootStore.ui.project.source
                : this.rootStore.config.source
          })
        : yield this.askWsLanguage({
            SessionId: this.rootStore.app.sessionId,
            ProjectId: configProjectId,
            Question,
            RecordQuestion: 'yes',
            TextLanguage,
            Source:
              this.rootStore.ui.project.source !== ''
                ? this.rootStore.ui.project.source
                : this.rootStore.config.source,
            ChannelId: this.rootStore.config.channel || 1
          });

      // if something went wrong and we got undefined
      // (need to check, but probably this condition will never fired)
      if (typeof response === 'undefined') {
        throw new Error('no response');
      }

      // one of the conditions is to check whether the message
      // entered by the user matches the prepared tags in the config
      // returns `true` || `false`
      // @todo add possibility to disable scenarios
      const inTags = checkQuestionInTags(
        Question,
        this.rootStore.config.liveAgentTriggerScenarios.tags
      );

      // check response from `askWsLanguage`
      // if it is `false` then we show an error and stop next sequence
      if (response === false) {
        // also check 2 scenarios:
        // if the message is in tags -> suggest live chat
        // if not -> increment "no answers" counter.
        if (inTags) {
          PubSub.publish(PUBSUB_LIVE_CHAT_SUGGEST);
        } else {
          this.noAnswersCount += 1;
        }

        // throw error to catch
        throw new Error('no response');
      }

      // MesId is overwritten below, so it is destructed separately
      const { Responses } = response;
      let { MesId } = response;
      const responseSourceLanguage = response.SourceLanguage;
      this.rootStore.chat.setLiveChatTriggers('', '');

      if (Responses) {
        // get the `Response` value from Responses
        const { Response } = Responses;

        // Response must be always array
        const ResponsesArray = Array.isArray(Response) ? Response : [Response];

        // internal notes (an additional text)
        const internalNotesText =
          this.rootStore.config.showInternalNotes &&
          this.rootStore.config.showInternalNotes.enabled &&
          ResponsesArray[0].InternalNotes
            ? ResponsesArray[0].InternalNotes
            : '';

        // insertChatHistory request
        if (
          this.rootStore.config.insertChatHistory &&
          this.rootStore.config.insertChatHistory.enabled
        ) {
          const insertQuestion = this.rootStore.config.sendEmail?.enabled ? '' : Question;

          const insertResponse = this.rootStore.config.sendEmail?.enabled
            ? `<p><strong>${this.rootStore.config.sendEmail.projectName} says:</strong></p><p>${ResponsesArray[0].Text}</p>`
            : ResponsesArray[0].Text;

          this.insertChatHistory({
            MesId: ResponsesArray[0].MesGroupId,
            Question: insertQuestion,
            Response: insertResponse
          });
        }

        this.resetNoAnswersCount();

        const intentForEnabledMessageInput = this.rootStore.config.messageInput.onInit
          ? this.rootStore.config.messageInput.onInit.intentOnEnable
          : 0;

        if (intentForEnabledMessageInput === ResponsesArray[0].MesGroupId) {
          this.rootStore.ui.setMessageInputDisabled(false);
        }

        // run runMqaSequence for a specific intent, if a specific input field exists
        const intentUserInput = this.detectIntentUserInput(ResponsesArray[0].MesGroupId);

        if (intentUserInput) {
          return yield this.runMqaSequence({
            MesId: intentUserInput,
            SourceLanguage: responseSourceLanguage,
            Source:
              this.rootStore.ui.project.source !== ''
                ? this.rootStore.ui.project.source
                : this.rootStore.config.source,
            SessionId: this.rootStore.app.sessionId,
            ProjectId: configProjectId,
            CrossProjectName: ''
          });
        }

        // Check live chat with special tag

        if (this.rootStore.config.liveAgentTriggerScenarios.scenarios.showOnTag) {
          const liveChatTag = this.rootStore.config.liveAgentTriggerScenarios.scenarios.showOnTag
            .tagName;
          const liveChatTagOnline = this.rootStore.config.liveAgentTriggerScenarios.scenarios
            .showOnTag.tagOnline;
          const liveChatTagOffline = this.rootStore.config.liveAgentTriggerScenarios.scenarios
            .showOnTag.tagOffline;
          const liveChatTriggerId = this.rootStore.config.liveAgentTriggerScenarios.scenarios
            .showOnTag.tagTriggerId;
          const liveChatCatId = this.rootStore.config.liveAgentTriggerScenarios.scenarios.showOnTag
            .tagCatId;
          const getAns = ResponsesArray[0].Text;
          if (getAns.includes(liveChatTag)) {
            const parser = new DOMParser();
            const html = parser.parseFromString(getAns, 'text/html');
            const ansObject = html.body;
            const onlineMsg = ansObject.querySelector(`#${liveChatTagOnline}`).innerHTML;
            const offlineMsg = ansObject.querySelector(`#${liveChatTagOffline}`).innerHTML;
            this.rootStore.chat.setLiveChatTag(onlineMsg, offlineMsg);

            const getNotes = ResponsesArray[0].InternalNotes;
            const htmlNotes = parser.parseFromString(getNotes, 'text/html');
            const notesObject = htmlNotes.body;
            const catId = notesObject.querySelector(`#${liveChatCatId}`)
              ? notesObject.querySelector(`#${liveChatCatId}`).innerHTML
              : '';
            const triggerId = notesObject.querySelector(`#${liveChatTriggerId}`)
              ? notesObject.querySelector(`#${liveChatTriggerId}`).innerHTML
              : '';
            this.rootStore.chat.setLiveChatTriggers(catId, triggerId);

            PubSub.publish(PUBSUB_LIVE_CHAT_SUGGEST);
            return null;
          }
        }

        // Suggest the live chat on special intent
        if (this.rootStore.config.liveAgentTriggerScenarios.scenarios) {
          const isSpecialIntentForLiveChat =
            ResponsesArray[0].MesGroupId ===
            this.rootStore.config.liveAgentTriggerScenarios.scenarios.showOnIntent
              ? this.rootStore.config.liveAgentTriggerScenarios.scenarios.showOnIntent
              : 0;
          if (isSpecialIntentForLiveChat) {
            window.messageFolderId = 0;
            PubSub.publish(PUBSUB_LIVE_CHAT_SUGGEST, responseSourceLanguage);
            return null;
          }
        }

        // Send wolframAlpha request if a message belongs to WolframAlpha
        const wolframAlphaResponse = ResponsesArray.find(item => item.Message === 'WolframAlpha');
        if (wolframAlphaResponse) {
          return yield this.wolframAlpha({
            Question
          });
        }

        // Send processCrossProject request if projectId !== config.id
        if (ProjectId && ProjectId !== configProjectId && MainProjectMesId) {
          yield this.processCrossProject({
            MainProjectMesId,
            CrossProjectMesId: response.MesId,
            MainProjectName: configProjectName
          });
        }

        // configurable number of related questions that are shown with the primary question
        const relatedQuestionsCount = Number(this.rootStore.config.relatedQuestions.maxCount);

        // If the first response has ProjectId !== current project id
        // and ProjectName === 'Central Module'
        // Or has very high level: FolderId === 0 and Message === 'Rephrase question'
        if (
          (ResponsesArray[0].ProjectId !== configProjectId &&
            ResponsesArray[0].ProjectName === constantsForRequest.centralModule) ||
          (ResponsesArray[0].FolderId === 0 &&
            ResponsesArray[0].IsTopMatch &&
            ResponsesArray[0].Message === constantsForRequest.rephraseQuestion)
        ) {
          this.rootStore.chat.addMessage(
            new HTMLMessage({
              content: ResponsesArray[0].Text,
              type: MESSAGE_TYPE_RECEIVED,
              original: {
                internalNotes: internalNotesText
              }
            })
          );
          if (
            ResponsesArray[0].Message === constantsForRequest.rephraseQuestion &&
            ResponsesArray.length > 1 &&
            this.rootStore.config.showSuggestedQuestions.enabled
          ) {
            this.showRelatedQuestions(
              'suggestedQuestions',
              ResponsesArray.slice(1, 4),
              response.MesId
            );
          }
        }

        // if the first response is cross projects answer
        if (
          !ProjectId &&
          ResponsesArray[0].ProjectId !== configProjectId &&
          ResponsesArray[0].ProjectName !== constantsForRequest.centralModule
        ) {
          yield this.recordCrossAgencyQuery({ MesId, TLA: this.rootStore.config.tla });
          this.showRelatedQuestionsProjects(ResponsesArray.slice(0, 4), response.MesId);
        } else {
          // if the first response is Medium Confidence and
          // Source Project (ProjectId === current project id)
          if (
            ResponsesArray[0].Confidence === 'MEDIUM' &&
            ResponsesArray[0].ProjectId === configProjectId
          ) {
            this.rootStore.chat.addMessage(
              new QuestionsMessage({
                title: 'mediumSourceRelatedQuestions', // i18n key
                type: MESSAGE_TYPE_RECEIVED,
                content: ResponsesArray
              })
            );
            return null;
          }

          // pick the first and other questions separately
          // the first question is used as the main message that is displayed to the user
          // other questions as related, they are shown in the list at the request of the user (button click)
          const [primaryQuestion, ...relatedQuestions] = ResponsesArray;

          // the original MesId does not match or is missing
          // so we use what in the primary question
          MesId = primaryQuestion.MesGroupId;

          // detect the isDateTime for question 'What is the time?'
          const isDateTime = primaryQuestion.Text.includes(constantsForRequest.dateTime);

          if (isDateTime) {
            primaryQuestion.Text = primaryQuestion.Text.replace(constantsForRequest.dateTime, '');
          }

          // is Response.Text is empty
          const isEmptyAnswer = detectEmptyAnswer(primaryQuestion.Text);

          // create HTML message for primary question
          if (
            primaryQuestion.Message !== constantsForRequest.rephraseQuestion &&
            primaryQuestion.ProjectName !== constantsForRequest.centralModule &&
            !isEmptyAnswer
          ) {
            // detect: if the answer contains a special 'id'
            const isTextWithSpecialId = this.detectMultiResponse(primaryQuestion.Text);

            const intentCarousel = this.detectIntentCarousel(MesId);
            const isIntentCarouselText = primaryQuestion.Text.includes(
              constantsForRequest.intentCarousel
            );

            // show intent with the carousel
            if (!isEmpty(intentCarousel) && !isIntentCarouselText) {
              return this.rootStore.chat.addMessage(
                new CarouselMessage({
                  type: MESSAGE_TYPE_RECEIVED,
                  content: {
                    content: intentCarousel.content
                  }
                })
              );
            }

            if (isTextWithSpecialId) {
              // create HTML messages with the special id
              const sourceLanguage =
                responseSourceLanguage === 'en' ? 'en-GB' : responseSourceLanguage;
              yield this.showMessageWithSpecialId(
                primaryQuestion,
                primaryQuestion.Text,
                ProjectName,
                MesId,
                response.MesId,
                [],
                sourceLanguage
              );
            } else {
              const message = new HTMLMessage({
                type: MESSAGE_TYPE_RECEIVED,
                original: {
                  // use re-assigned MesId and primaryQuestion
                  // as `original` for this message
                  ...primaryQuestion,
                  MesId,
                  FeedbackMesId: response.MesId,
                  crossProjectName: ProjectName,
                  isDateTime,
                  internalNotes: internalNotesText
                },
                // show feedback when FolderId !== 0 && IsTopMatch (This 2 condition is good enough)
                hasFeedback: isPriorityMessage(primaryQuestion),
                content: primaryQuestion.Text
              });

              // show the first message
              this.rootStore.chat.addMessage(message);
            }

            // show a button with next list of RelatedQuestions
            if (
              relatedQuestions.length &&
              relatedQuestionsCount > 0 &&
              this.rootStore.config.relatedQuestions.button.enabled === true
            ) {
              // select related questions array
              this.relatedQuestionsForButton = relatedQuestions;
            }

            localStorage.setItem('rightQuestionClicked', false);
          }
        }

        if (inTags) {
          PubSub.publish(PUBSUB_LIVE_CHAT_SUGGEST);
        } else {
          this.setLastResponseId(ResponsesArray.ResponseId); // @todo use mesGroupId?
        }

        // ⭐ [2] run gpf sequence
        if (!isEmpty(multilevelFlowData)) {
          yield this.runTreeSequence({
            ProjectId: configProjectId,
            MesId,
            GPFId: multilevelFlowData.gpfId,
            ParentNodeId: multilevelFlowData.parentNodeId,
            SourceLanguage: responseSourceLanguage,
            CrossProjectName: ProjectName,
            hideEmptyAnswer: true
          });
        } else {
          // run -> gpfId -> gpfTree ...
          yield this.runGpfSequence({
            ProjectId: configProjectId,
            MesId,
            Source:
              this.rootStore.ui.project.source !== ''
                ? this.rootStore.ui.project.source
                : this.rootStore.config.source,
            SourceLanguage: responseSourceLanguage,
            SessionId: this.rootStore.app.sessionId,
            CrossProjectName: ProjectName
          });
        }

        // Show 'Relate Questions' list only after gpfTree
        if (this.relatedQuestionsForButton.length > 0) {
          this.rootStore.chat.addMessage(
            new RelatedQuestionsButton({
              type: MESSAGE_TYPE_RECEIVED,
              content: this.relatedQuestionsForButton.slice(0, relatedQuestionsCount)
            })
          );
          this.relatedQuestionsForButton = [];
        }
      } else {
        // ⭐ [3] show special message for 'no answer'
        // (if we do not have an object with response)

        // suggest live chat if inTags
        if (inTags) {
          PubSub.publish(PUBSUB_LIVE_CHAT_SUGGEST);
          return;
        }

        this.noAnswersCount += 1;
        const isEscalationMessage =
          this.noAnswersCount >= Number(this.rootStore.config.escalationMessages.noAnswersCount);
        const keyWords = Question.split(' ');

        if (!isEscalationMessage) {
          // If user message contains many words
          if (keyWords.length !== 1) {
            this.rootStore.chat.addMessage(
              new TextMessageWithTranslation({
                type: MESSAGE_TYPE_RECEIVED,
                content:
                  this.rootStore.config.escalationMessages.keyWord.manyWordsTranslationId ||
                  'I am sorry, I am unable to help you with this question. Please rephrase your question.'
              })
            );
            if (this.rootStore.config.insertChatHistory?.enabled) {
              const insertQuestion = this.rootStore.config.sendEmail?.enabled ? '' : Question;

              const insertResponse = this.rootStore.config.sendEmail?.enabled
                ? `<p><strong>${this.rootStore.config.sendEmail.projectName} says:</strong></p><p>I am sorry, I am unable to help you with this question. Please rephrase your question.</p>`
                : 'I am sorry, I am unable to help you with this question. Please rephrase your question.';

              this.insertChatHistory({
                Question: insertQuestion,
                Response: insertResponse
              });
            }
          } else {
            // If user message contains one word
            this.rootStore.chat.addMessage(
              new TextMessageWithTranslation({
                type: MESSAGE_TYPE_RECEIVED,
                content:
                  this.rootStore.config.escalationMessages.keyWord.oneWordTranslationId ||
                  'I note that you have only typed keywords. I can provide a better response to a full question. Please ask again'
              })
            );
            if (this.rootStore.config.insertChatHistory?.enabled) {
              const insertQuestion = this.rootStore.config.sendEmail?.enabled ? '' : Question;

              const insertResponse = this.rootStore.config.sendEmail?.enabled
                ? `<p><strong>${this.rootStore.config.sendEmail.projectName} says:</strong></p><p>I note that you have only typed keywords. I can provide a better response to a full question. Please ask again.</p>`
                : 'I note that you have only typed keywords. I can provide a better response to a full question. Please ask again.';

              this.insertChatHistory({
                Question: insertQuestion,
                Response: insertResponse
              });
            }
          }
        }
      }
    } catch (error) {
      // log any errors
      this.rootStore.app.log({ Message: handleError(error) });
    } finally {
      // hide messaging animation
      this.rootStore.chat.isMessaging = false;
    }
  });

  processFeedback = flow(function* processFeedback({
    ProjectId,
    MesId,
    Feedback,
    MesGroupID,
    SessionId
  }) {
    const response = yield API.post(
      endpoints.paths.processFeedback,
      querystring.stringify({
        ProjectId,
        MesId,
        Feedback,
        MesGroupID,
        SessionId
      })
    );

    if (response && response.int) {
      return response.int === 1;
    }

    return false;
  });

  getSuggestions = flow(function* getSuggestions(
    { ProjectId, SuggQuesCount, SearchText },
    cancelToken
  ) {
    const isSuggestionByCategory =
      this.rootStore.config.getSuggestedQuestionsbyCategory &&
      this.rootStore.config.getSuggestedQuestionsbyCategory.enabled;

    const getExclude = value => {
      if (value) {
        return 1;
      }

      return 0;
    };

    const categoryId = isSuggestionByCategory
      ? this.rootStore.config.getSuggestedQuestionsbyCategory.category
      : 0;
    const exclude = isSuggestionByCategory
      ? getExclude(this.rootStore.config.getSuggestedQuestionsbyCategory.exclude)
      : 0;
    const endpoint = isSuggestionByCategory
      ? endpoints.paths.getSuggestedQuestionsbyCategory
      : endpoints.paths.getSuggestedQuestions;

    try {
      const response = yield API.post(
        endpoint,
        querystring.stringify({
          ProjectId,
          SuggQuesCount,
          SearchText,
          Folders: categoryId,
          Exclude: exclude
        }),
        { cancelToken }
      );

      if (response && response.ArrayOfGPFColl && response.ArrayOfGPFColl.GPFColl) {
        const { GPFColl } = response.ArrayOfGPFColl;
        return Array.isArray(GPFColl) ? GPFColl : [GPFColl];
      }

      return [];
    } catch (e) {
      return [];
    }
  });

  getMqaLanguageForCarousel = flow(function* getMqaLanguageForCarousel({
    ProjectId,
    MesId,
    Source,
    SessionId,
    CrossProjectName,
    Question,
    TextLanguage
  }) {
    // display the question
    this.rootStore.chat.addMessage(
      new HTMLMessage({
        type: MESSAGE_TYPE_SENT,
        content: Question,
        original: {
          crossProjectName: CrossProjectName
        }
      })
    );
    this.rootStore.chat.isMessaging = true;

    // receive an answer
    try {
      const mqa = yield this.getMqa({
        ProjectId,
        MesId,
        Source,
        SessionId,
        CrossProjectName,
        TextLanguage
      });

      if (mqa && mqa.ResponseText) {
        const isTextWithSpecialId = this.detectMultiResponse(mqa.ResponseText);
        const internalNotesText =
          this.rootStore.config.showInternalNotes &&
          this.rootStore.config.showInternalNotes.enabled &&
          mqa.ResponseTextNotes
            ? mqa.ResponseTextNotes
            : '';

        if (isTextWithSpecialId) {
          // create HTML messages with the special id
          yield this.showMessageWithSpecialId(
            mqa,
            mqa.ResponseText,
            CrossProjectName,
            mqa.MesID,
            0
          );
        } else {
          setTimeout(() => {
            this.rootStore.chat.addMessage(
              new HTMLMessage({
                type: MESSAGE_TYPE_RECEIVED,
                content: mqa.ResponseText,
                original: {
                  ...mqa,
                  crossProjectName: CrossProjectName,
                  internalNotes: internalNotesText
                }
              })
            );
          }, 300);
        }
      }

      yield this.runGpfSequence({
        ProjectId,
        MesId,
        Source:
          this.rootStore.ui.project.source !== ''
            ? this.rootStore.ui.project.source
            : this.rootStore.config.source,
        SourceLanguage: this.rootStore.app.currentLanguage,
        SessionId: this.rootStore.app.sessionId,
        CrossProjectName
      });

      // insertChatHistory request
      if (
        this.rootStore.config.insertChatHistory &&
        this.rootStore.config.insertChatHistory.enabled
      ) {
        this.insertChatHistory({ MesId, Question: mqa.MessageText, Response: mqa.ResponseText });
      }
    } catch (error) {
      this.rootStore.app.log({ Message: handleError(error) });
    } finally {
      this.rootStore.chat.isMessaging = false;
    }
  });

  processSendForm = flow(function* processSendForm(requestParameters, mesId, question) {
    this.rootStore.chat.addMessage(
      new HTMLMessage({
        type: MESSAGE_TYPE_SENT,
        content: question
      })
    );

    const intentForEnabledMessageInput = this.rootStore.config.messageInput.onInit
      ? this.rootStore.config.messageInput.onInit.intentOnEnable
      : 0;

    if (intentForEnabledMessageInput === Number(mesId)) {
      this.rootStore.ui.setMessageInputDisabled(false);
    }

    const questionText = this.rootStore.config.sendEmail?.enabled
      ? `<p><strong>User Details:</strong></p><strong>Name:</strong> ${
          requestParameters.input_name
        }<br><strong>Email:</strong> ${
          requestParameters.input_email
        }<br><strong>Contact:</strong> ${requestParameters.input_contact || 'NA'}`
      : Object.values(requestParameters).join();

    try {
      yield this.runMqaSequence({
        MesId: mesId,
        SourceLanguage: this.rootStore.app.currentLanguage || 'en',
        Source:
          this.rootStore.ui.project.source !== ''
            ? this.rootStore.ui.project.source
            : this.rootStore.config.source,
        SessionId: this.rootStore.app.sessionId,
        ProjectId:
          this.rootStore.ui.project.projectId > 0
            ? this.rootStore.ui.project.projectId
            : this.rootStore.config.id,
        CrossProjectName: '',
        Question: questionText
      });
    } catch (error) {
      this.rootStore.app.log({ Message: handleError(error) });
    } finally {
      let checkedRequestPerameters;

      if (
        this.rootStore.config.messageInput.regexes &&
        this.rootStore.config.messageInput.regexes.enabled &&
        this.rootStore.config.messageInput.regexes.items.length > 0
      ) {
        const stringRequestPerameters = JSON.stringify(requestParameters);
        const checkedParameters = handleRegex(
          this.rootStore.config.messageInput.regexes.items,
          stringRequestPerameters
        );
        checkedRequestPerameters = JSON.parse(checkedParameters);
      } else {
        checkedRequestPerameters = { ...requestParameters };
      }

      if (this.rootStore.config.sendEmail?.enabled) {
        setTimeout(() => {
          const myHeaders = new Headers();
          myHeaders.append('Content-Type', 'application/json');

          const raw = JSON.stringify({
            sessionId: this.rootStore.app.sessionId,
            ProjectId: this.rootStore.config.id
          });

          const requestOptions = {
            method: 'POST',
            headers: myHeaders,
            body: raw,
            redirect: 'follow'
          };

          fetch(endpoints.paths.generateChatHistory, requestOptions)
            .then(response => response.json())
            .then(result => {
              const chatLink = result.TranScript;
              const seHeaders = new Headers();
              seHeaders.append('Content-Type', 'application/json');

              const seRaw = JSON.stringify({
                sessionId: this.rootStore.app.sessionId,
                name: checkedRequestPerameters.input_name,
                email: checkedRequestPerameters.input_email,
                contact: checkedRequestPerameters.input_contact || 'NA',
                projectName: this.rootStore.config.sendEmail.projectName,
                locale: 'en-gb',
                transcript: chatLink
              });

              const seRequestOptions = {
                method: 'POST',
                headers: seHeaders,
                body: seRaw,
                redirect: 'follow'
              };

              fetch(endpoints.paths.sendEmail, seRequestOptions)
                .then(seResponse => seResponse.text())
                .then(seResult => console.log(seResult))
                .catch(seError => console.log('error', seError));
            });
        }, 1000);
      } else {
        yield API.post(
          endpoints.paths.insertVAInputs,
          JSON.stringify({
            IDName:
              this.rootStore.ui.project.source !== ''
                ? this.rootStore.ui.project.source
                : this.rootStore.config.source,
            ProjectID:
              this.rootStore.ui.project.projectId > 0
                ? this.rootStore.ui.project.projectId
                : this.rootStore.config.id,
            SessionId: this.rootStore.app.sessionId,
            ...checkedRequestPerameters
          })
        );
      }
    }
  });

  wolframAlpha = flow(function* wolframAlpha({ Question }) {
    const pathWithQuestion = endpoints.paths.wolframAlpha + Question;

    try {
      const response = yield API.get(pathWithQuestion);

      if (response) {
        if (
          response.queryresult.attr_success &&
          response.queryresult.pod.length &&
          response.queryresult.pod[1]
        ) {
          const choosenResponse = response.queryresult.pod[1];
          const imageResponse = response.queryresult.pod
            .slice()
            .filter(item => item.attr_title === 'Image');

          const answerText = choosenResponse.subpod.plaintext;
          const answerImage = imageResponse.length ? imageResponse[0].subpod.img : null;

          return this.rootStore.chat.addMessage(
            new HTMLMessage({
              type: MESSAGE_TYPE_RECEIVED,
              content: {
                text: answerText,
                image: answerImage,
                question: Question
              },
              original: {
                isWolframAlphaMessage: true
              }
            })
          );
        }
      }

      return this.rootStore.chat.addMessage(
        new TextMessageWithTranslation({
          type: MESSAGE_TYPE_RECEIVED,
          content: this.rootStore.config.escalationMessages.keyWord.manyWordsTranslationId
        })
      );
    } catch (e) {
      return this.rootStore.chat.addMessage(
        new TextMessageWithTranslation({
          type: MESSAGE_TYPE_RECEIVED,
          content: this.rootStore.config.escalationMessages.keyWord.manyWordsTranslationId
        })
      );
    }
  });

  runGetMqaLanguageWithSpecialIntent = flow(function* runGetMqaLanguageWithSpecialIntent(
    intent,
    language
  ) {
    this.isSpecialIntent = intent;

    try {
      const mqa = yield this.getMqa({
        ProjectId:
          this.rootStore.ui.project.projectId > 0
            ? this.rootStore.ui.project.projectId
            : this.rootStore.config.id,
        Source:
          this.rootStore.ui.project.source !== ''
            ? this.rootStore.ui.project.source
            : this.rootStore.config.source,
        MesId: intent,
        SessionId: this.rootStore.app.sessionId,
        CrossProjectName: '',
        TextLanguage: language
      });

      if (mqa && mqa.ResponseText) {
        this.rootStore.chat.addMessage(
          new HTMLMessageWithScroll({
            type: MESSAGE_TYPE_RECEIVED,
            content: mqa.ResponseText,
            original: {
              ...mqa,
              crossProjectName: ''
            }
          })
        );
      }

      yield this.runGpfSequence({
        ProjectId:
          this.rootStore.ui.project.projectId > 0
            ? this.rootStore.ui.project.projectId
            : this.rootStore.config.id,
        Source:
          this.rootStore.ui.project.source !== ''
            ? this.rootStore.ui.project.source
            : this.rootStore.config.source,
        MesId: intent,
        SessionId: this.rootStore.app.sessionId,
        SourceLanguage: language,
        CrossProjectName: ''
      });

      if (
        this.rootStore.config.insertChatHistory &&
        this.rootStore.config.insertChatHistory.enabled
      ) {
        const insertResponse = this.rootStore.config.sendEmail?.enabled
          ? `<p><strong>${this.rootStore.config.sendEmail.projectName} says:</strong></p><p>${mqa.ResponseText}</p>`
          : mqa.ResponseText;

        this.insertChatHistory({
          MesId: intent,
          Question: '',
          Response: insertResponse
        });
      }

      return null;
    } catch (e) {
      return null;
    } finally {
      this.rootStore.chat.isMessaging = false;
    }
  });

  runIntentOnInit = flow(function* runIntentOnInit() {
    try {
      yield this.runMqaSequence({
        ProjectId:
          this.rootStore.ui.project.projectId > 0
            ? this.rootStore.ui.project.projectId
            : this.rootStore.config.id,
        Source:
          this.rootStore.ui.project.source !== ''
            ? this.rootStore.ui.project.source
            : this.rootStore.config.source,
        MesId: this.rootStore.config.intentOnInit.intent,
        SourceLanguage: this.rootStore.app.currentLanguage || 'en',
        SessionId: this.rootStore.app.sessionId,
        CrossProjectName: ''
      });
    } catch (error) {
      this.rootStore.app.log({ Message: handleError(error) });
    } finally {
      this.rootStore.chat.isMessaging = false;
    }
  });

  recordSessionEvent = flow(function* recordSessionEvent(agent) {
    const languageId = detectLanguageId(this.rootStore.app.currentLanguage);

    const response = yield API.post(
      endpoints.paths.recordSessionEvent,
      JSON.stringify({
        SessionEventType: 2,
        ProjectId:
          this.rootStore.ui.project.projectId > 0
            ? this.rootStore.ui.project.projectId
            : this.rootStore.config.id,
        Source:
          this.rootStore.ui.project.source !== ''
            ? this.rootStore.ui.project.source
            : this.rootStore.config.source,
        ComponentType: 1,
        MesId: '',
        MesgroupId: this.isSpecialIntent,
        GPFId: '',
        Destination: agent.toLowerCase(),
        SessionId: this.rootStore.app.sessionId,
        Brand:
          this.rootStore.ui.project.source !== ''
            ? this.rootStore.ui.project.source
            : this.rootStore.config.source,
        SessionEnded: false,
        LanguageId: languageId,
        ActionType: 0,
        Outcome: 1
      })
    );

    this.isSpecialIntent = 0;

    return response;
  });

  jobSearch = flow(function* jobSearch() {
    const jobTitle = this.formFields.input_jobtitle.replaceAll('&', '%26');
    const industry = this.formFields.input_industry.replaceAll('&', '%26');
    const path = `${endpoints.paths.jobSearch}?Search=${jobTitle}&Industry=${industry}&Seniority=${this.formFields.input_seniority}&Salary=${this.formFields.input_salary}`;

    this.rootStore.chat.isMessaging = true;
    const response = yield API.get(path);

    const dataResponse = response.Responses[0].Response
      ? response.Responses[0].Response
      : response.Responses;
    return dataResponse;
  });

  jsonFile = flow(function* jsonFile(path) {
    const response = yield API.get(path);

    return response;
  });

  /**
   * @todo move to message model?
   */
  @action like({ ProjectId, FeedbackMesId, MesGroupId: MesGroupID }) {
    this.processFeedback({
      ProjectId,
      SessionId: this.rootStore.app.sessionId,
      Feedback: 200,
      MesId: FeedbackMesId,
      MesGroupID
    });
  }

  /**
   * @todo move to message model?
   */
  @action dislike({ ProjectId, FeedbackMesId, MesGroupId: MesGroupID, FolderId, ParentFolderId }) {
    this.processFeedback({
      ProjectId,
      SessionId: this.rootStore.app.sessionId,
      MesId: FeedbackMesId,
      MesGroupID,
      Feedback: 201
    });

    if (
      this.rootStore.config.liveAgentTriggerScenarios.scenarios &&
      this.rootStore.config.liveAgentTriggerScenarios.scenarios.showIfNegativeFeedback
    ) {
      window.messageFolderId = ParentFolderId || FolderId;
      PubSub.publish(PUBSUB_LIVE_CHAT_SUGGEST);
    }

    if (
      this.rootStore.config.liveAgentTriggerScenarios.scenarios &&
      this.rootStore.config.liveAgentTriggerScenarios.scenarios.showIfTDFeedback
    ) {
      window.messageFolderId = ParentFolderId || FolderId;
      PubSub.publish(PUBSUB_LIVE_CHAT_SUGGEST_THUMBSDOWN);
    }

    if (this.rootStore.config.escalationMessages.intentNegativeFeedback) {
      this.runGetMqaLanguageWithSpecialIntent(
        this.rootStore.config.escalationMessages.intentNegativeFeedback
      );
    }
  }

  /**
   * @todo move to message model?
   */
  @action showRelatedQuestions(title, relatedQuestions = []) {
    this.rootStore.chat.addMessage(
      new QuestionsMessage({
        title: title || 'relatedQuestions', // i18n key
        type: MESSAGE_TYPE_RECEIVED,
        content: relatedQuestions
      })
    );
  }

  @action showRelatedQuestionsProjects(relatedQuestions = [], MainProjectMesId) {
    const firstAnswerProjectName = relatedQuestions[0].ProjectName;
    const isOneAgency = relatedQuestions.every(
      question => question.ProjectName === firstAnswerProjectName
    );
    this.rootStore.chat.addMessage(
      new QuestionsMessageProjects({
        title: isOneAgency ? 'relatedQuestionsProject' : 'relatedQuestionsProjects', // i18n key
        type: MESSAGE_TYPE_RECEIVED,
        content: relatedQuestions,
        original: {
          MainProjectMesId
        }
      })
    );
  }

  /**
   * function for checking 'id' and showing the carousel,
   * if the special id exists
   */
  @action detectIntentCarousel(mesId) {
    if (this.rootStore.config.intentCarousel && this.rootStore.config.intentCarousel.length) {
      const currentIntent = this.rootStore.config.intentCarousel.find(
        item => item.intent === mesId
      );
      return currentIntent || {};
    }

    return {};
  }

  /**
   * function for checking 'id' and showing messages,
   * if the special id exists
   */
  @action detectMultiResponse(text) {
    if (!this.rootStore.config.multiResponse) return false;

    const multiResponseId = this.rootStore.config.multiResponse.id
      ? `id="${this.rootStore.config.multiResponse.id}`
      : null;

    if (!multiResponseId) return false;

    const isSpecialText = this.rootStore.config.multiResponse.id && text.includes(multiResponseId);
    return isSpecialText;
  }

  @action showMessageWithSpecialId(
    currentMessage,
    currentText,
    CrossProjectName,
    mesId,
    FeedbackMesId,
    jobSearchContent,
    responseSourceLanguage
  ) {
    const multiResponseId = this.rootStore.config.multiResponse.id;
    const newMessages = separateMessageText(currentText, multiResponseId);
    const intentCarousel = this.detectIntentCarousel(mesId);
    const isJobSearchCarousel = this.detectJobSearch(mesId);

    return new Promise(resolve => {
      newMessages.map((mes, index) => {
        currentText = mes;

        const feedback =
          currentMessage.FolderId !== 0 && index === newMessages.length - 1
            ? isPriorityMessage(currentMessage)
            : false;

        let message;

        if (currentText.includes(constantsForRequest.intentCarouselWithJsonData)) {
          // display an intent carousel from the response
          const newDom = new DOMParser().parseFromString(currentText, 'text/html');
          const bubbleElemet = newDom.querySelector('div');
          const jsonData = bubbleElemet.dataset.json.replaceAll("'", '"');
          const carouselData = JSON.parse(jsonData).map(item => {
            item.mesId = Number(item.mesId);
            return item;
          });

          message = new CarouselMessage({
            type: MESSAGE_TYPE_RECEIVED,
            original: {
              isShowMessageDate: index !== 0,
              sourceLanguage: responseSourceLanguage
            },
            content: {
              hotTopics: this.hotTopics,
              popularQuestions: this.topQuestions,
              content: carouselData
            }
          });
        } else if (
          !isEmpty(intentCarousel) &&
          currentText.includes(constantsForRequest.intentCarousel)
        ) {
          // show the carousel for special intent
          message = new CarouselMessage({
            type: MESSAGE_TYPE_RECEIVED,
            original: {
              isShowMessageDate: index !== 0,
              sourceLanguage: responseSourceLanguage
            },
            content: {
              content: intentCarousel.content
            }
          });
        } else if (isJobSearchCarousel && currentText.includes(constantsForRequest.jobSearch)) {
          // show the carousel with jobSearch
          message = new CarouselMessage({
            type: MESSAGE_TYPE_RECEIVED,
            original: {
              isShowMessageDate: index !== 0,
              jobSearch: true
            },
            content: {
              content: jobSearchContent
            }
          });
        } else if (
          currentText.includes(constantsForRequest.jobSearchLink) &&
          this.rootStore.config.jobSearch.industries &&
          this.rootStore.config.jobSearch.industries.length
        ) {
          // add special jobSearch link
          const linkUrl = this.rootStore.config.jobSearch.industries.find(
            item => item[this.formFields.input_industry]
          )[`${this.formFields.input_industry}`];
          const customLink =
            this.formFields.input_seniority === 'NA'
              ? `<a href="${linkUrl}search=${this.formFields.input_jobtitle}&salary=${this.formFields.input_salary}&sortBy=new_posting_date&page=0" target="_blank">here</a>`
              : `<a href="${linkUrl}search=${this.formFields.input_jobtitle}&salary=${this.formFields.input_salary}&positionLevel=${this.formFields.input_seniority}&sortBy=new_posting_date&page=0" target="_blank">here</a>`;

          const changedText = currentText.replace(constantsForRequest.jobSearchLink, customLink);

          message = new HTMLMessage({
            type: MESSAGE_TYPE_RECEIVED,
            original: {
              ...currentMessage,
              MesId: mesId,
              FeedbackMesId,
              crossProjectName: CrossProjectName,
              isShowMessageDate: index !== 0
            },
            hasFeedback: feedback,
            content: changedText
          });
        } else {
          message = new HTMLMessage({
            type: MESSAGE_TYPE_RECEIVED,
            original: {
              // use re-assigned MesId and primaryQuestion
              // as `original` for this message
              ...currentMessage,
              MesId: mesId,
              FeedbackMesId,
              crossProjectName: CrossProjectName,
              isShowMessageDate: index !== 0
            },
            hasFeedback: feedback,
            content: currentText
          });
        }

        const { timeout } = this.rootStore.config.message.animation;
        const delay = this.rootStore.config.multiResponse.animationDelay || 300;
        const messageTimeout = index === 0 ? timeout : delay * index + timeout;

        return setTimeout(() => {
          this.rootStore.chat.addMessage(message);
          if (index === newMessages.length - 1) {
            resolve();
          }
        }, messageTimeout);
      });
    });
  }

  /**
   * function for checking 'id' and a needed input field
   * for showing messages with special intent
   */
  @action detectIntentUserInput(responseMesId) {
    if (this.rootStore.config.intentUserInput && this.rootStore.config.intentUserInput.enabled) {
      const currentIntent = this.rootStore.config.intentUserInput.userSelection.find(
        item => item.intent === responseMesId
      );
      if (!isEmpty(currentIntent)) {
        const value = this.formFields[currentIntent.inputName];
        const emptyValue = currentIntent.values.find(item => item.selectionValue === '');

        if (value) {
          const configIntent = currentIntent.values.find(item => item.selectionValue === value);
          const intentForRequest = configIntent ? configIntent.displayIntent : 0;
          return intentForRequest;
        }
        if (!isEmpty(emptyValue)) {
          const intentForRequest = emptyValue.displayIntent;
          return intentForRequest;
        }

        return 0;
      }
    }
    return 0;
  }

  /**
   * function for checking 'mesId' of jobSearch
   */
  @action detectJobSearch(mesId) {
    if (this.rootStore.config.jobSearch && this.rootStore.config.jobSearch.enabled) {
      const isIntent = this.rootStore.config.jobSearch.intents.find(item => item === mesId);
      return isIntent;
    }

    return 0;
  }

  /**
   * function for checking specialJsonSelect
   */
  @action detecttJsonSelect(mesId) {
    if (
      this.rootStore.config.specialJsonSelect &&
      this.rootStore.config.specialJsonSelect.enabled
    ) {
      const intent = this.rootStore.config.specialJsonSelect.selects.find(
        item => item.intent === mesId
      );
      return intent;
    }

    return {};
  }

  /**
   * render mqa data for each item of data
   */
  getMqaCarouselData = flow(function* getMqaCarouselData(data, language) {
    const intentsData = data.filter(item => item.mesId && item.mesId !== 0);
    if (!intentsData.length) return data;

    const newData = data.filter(item => !item.mesId || item.mesId === 0);
    for (let i = 0; i < intentsData.length; i += 1) {
      const intent = intentsData[i];
      const mqa = yield this.getMqaLanguageCarousel({
        ProjectId:
          this.rootStore.ui.project.projectId > 0
            ? this.rootStore.ui.project.projectId
            : this.rootStore.config.id,
        Source:
          this.rootStore.ui.project.source !== ''
            ? this.rootStore.ui.project.source
            : this.rootStore.config.source,
        MesId: intent.mesId,
        SessionId: this.rootStore.app.sessionId,
        CrossProjectName: '',
        TextLanguage: language
      });

      if (!isEmpty(mqa)) {
        const internalNotesText =
          this.rootStore.config.showInternalNotes &&
          this.rootStore.config.showInternalNotes.enabled &&
          mqa.ResponseTextNotes
            ? mqa.ResponseTextNotes
            : '';

        const newIntent = {
          ...intent,
          intentData: {
            title: mqa.MessageText,
            mesId: mqa.MesID,
            text: mqa.ResponseText,
            internalNotes: internalNotesText
          }
        };

        newData.push(newIntent);
      }
    }

    return newData;
  });
}
